import { useEffect } from 'react';

const SITE_NAME = 'CalculateEasy';
const SITE_URL = 'https://calculateeasytools.com';
const DEFAULT_DESCRIPTION = 'Free online calculators for percentages, dates, loans, tips, unit conversion and more — accurate, mobile-friendly, and ad-free at the point of use.';

const setMeta = (name, content, attr = 'name') => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = document.head.querySelector(`script[data-jsonld="${id}"]`);
  if (data == null) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-jsonld', id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

/**
 * SEO hook. Sets document title, meta description, canonical, Open Graph, and optional JSON-LD.
 */
export function useSeo({ title, description, path, jsonLd, robots = 'index,follow' }) {
  const serializedJsonLd = JSON.stringify(jsonLd || null);

  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Free Online Calculators & Tools`;
    document.title = fullTitle;
    const desc = description || DEFAULT_DESCRIPTION;
    setMeta('description', desc);
    setMeta('og:title', fullTitle, 'property');
    setMeta('og:description', desc, 'property');
    setMeta('og:type', 'website', 'property');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', desc);
    setMeta('robots', robots);
    const canonical = SITE_URL + (path || (typeof window !== 'undefined' ? window.location.pathname : ''));
    setLink('canonical', canonical);
    setMeta('og:url', canonical, 'property');
    setJsonLd('page', JSON.parse(serializedJsonLd));
    return () => setJsonLd('page', null);
  }, [title, description, path, robots, serializedJsonLd]);
}
